import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { Icon } from '../../foundation/primitives';
import { JudgementModal, StartGate, FailOverlay, MinigameHud } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';

const Template11ContradictionMatch = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_contradiction_11', templateId = 11 }) => {
  const defaultConfig = {
    title: '矛盾突き付け',
    statements: [
      { id: 's1', text: '私はその夜、家にいた' },
      { id: 's2', text: '鍵はかけたままだった' }
    ],
    evidence: [
      { id: 'e1', text: '監視カメラの映像' },
      { id: 'e2', text: '合鍵の記録' }
    ],
    correctPairs: [{ statementId: 's1', evidenceId: 'e1' }],
    maxMistakes: 2
  };
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const next = { ...defaultConfig, ...(hostConfig ?? {}) };
    if (!Array.isArray(next.statements) || next.statements.length === 0) {
      next.statements = defaultConfig.statements;
    }
    if (!Array.isArray(next.evidence) || next.evidence.length === 0) {
      next.evidence = defaultConfig.evidence;
    }
    if (!Array.isArray(next.correctPairs) || next.correctPairs.length === 0) {
      next.correctPairs = defaultConfig.correctPairs;
    }
    return next;
  }, [configKey]);
  const backgroundStyle = useMinigameBackground(hostConfig);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [foundPairs, setFoundPairs] = useState([]);
  const [mistakes, setMistakes] = useState(0);
  const [gameState, setGameState] = useState('playing');
  const [feedback, setFeedback] = useState(null);
  const [showEffect, setShowEffect] = useState(null);
  const [showObjection, setShowObjection] = useState(false);
  // R1 体験文法: 開始ゲート（hostMeta.ui.showTutorial !== false で表示）と retryCount
  const [gateOpen, setGateOpen] = useState(hostMeta?.ui?.showTutorial !== false);
  const [retryCount, setRetryCount] = useState(0);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const initializedRef = useRef(false);

  // R1 実動タイマー: config 固有（timeLimitSec）?? hostMeta.timeLimitMs（0 は「未設定」）。
  // 設定時のみ HUD に表示し、満了で失敗にする。ゲート表示中・決着後は停止する。
  const timeLimitMs =
    typeof resolvedConfig.timeLimitSec === 'number' && resolvedConfig.timeLimitSec > 0
      ? resolvedConfig.timeLimitSec * 1000
      : typeof hostMeta?.timeLimitMs === 'number' && hostMeta.timeLimitMs > 0
        ? hostMeta.timeLimitMs
        : null;
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs: timeLimitMs,
    running: gameState === 'playing' && !gateOpen,
    onExpire: () => setGameState('fail')
  });

  // R1 ミス上限: config 明示値（0=無制限の従来解釈を温存）?? hostMeta.maxMistakes（>0 のみ）?? 既定 2。
  const effectiveMaxMistakes =
    typeof hostConfig?.maxMistakes === 'number'
      ? hostConfig.maxMistakes
      : typeof hostMeta?.maxMistakes === 'number' && hostMeta.maxMistakes > 0
        ? hostMeta.maxMistakes
        : defaultConfig.maxMistakes;

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setSelectedStatement(null);
    setSelectedEvidence(null);
    setFoundPairs([]);
    setMistakes(0);
    setGameState('playing');
    setFeedback(null);
    setShowEffect(null);
    setShowObjection(false);
    resetSubmission();
  }, [resetSubmission]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    if (!selectedStatement || !selectedEvidence) return;
    const isCorrect = resolvedConfig.correctPairs.some(
      (pair) => pair.statementId === selectedStatement && pair.evidenceId === selectedEvidence
    );
    if (isCorrect) {
      const key = `${selectedStatement}::${selectedEvidence}`;
      if (!foundPairs.includes(key)) {
        const nextFound = [...foundPairs, key];
        setFoundPairs(nextFound);
        setShowObjection(true);
        setShowEffect('objection');
        setTimeout(() => {
          setShowObjection(false);
          setShowEffect(null);
        }, 1000);

        if (nextFound.length >= resolvedConfig.correctPairs.length) {
          setTimeout(() => {
            setShowEffect('success');
            setGameState('success');
          }, 1200);
        } else {
          setFeedback({ type: 'success', message: '矛盾を一つ突き付けた' });
        }
      }
      setSelectedStatement(null);
      setSelectedEvidence(null);
      return;
    }
    setShowEffect('fail');
    setFeedback({ type: 'error', message: 'その組み合わせに矛盾はない' });
    setTimeout(() => setShowEffect(null), 500);
    setSelectedStatement(null);
    setSelectedEvidence(null);
    setMistakes((prev) => {
      const next = prev + 1;
      if (effectiveMaxMistakes && next >= effectiveMaxMistakes) {
        setTimeout(() => setGameState('fail'), 600);
      }
      return next;
    });
  }, [selectedStatement, selectedEvidence, resolvedConfig, gameState, foundPairs, effectiveMaxMistakes]);

  const reset = () => {
    setSelectedStatement(null);
    setSelectedEvidence(null);
    setFoundPairs([]);
    setMistakes(0);
    setGameState('playing');
    setFeedback(null);
    setShowEffect(null);
    setShowObjection(false);
    // R1: やり直しは retryCount に積算し（全 submit payload に同梱）、タイマーも計り直す
    setRetryCount((prev) => prev + 1);
    resetTimer();
    resetSubmission();
  };

  const title = resolvedConfig.title ?? '矛盾突き付け';

  return (
    <div className="relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {/* R1: 独自 HUD（下寄せの題＋進捗テキスト）を MinigameHud semantic へ置換。
          timer は設定時のみ表示（TensionGauge 段階色）。? ボタンでゲート再表示。 */}
      <MinigameHud
        title={title}
        icon="Scale"
        timer={remainingMs != null && timeLimitMs != null ? { remainingMs, totalMs: timeLimitMs } : null}
        progress={{ current: foundPairs.length, total: resolvedConfig.correctPairs.length }}
        mistakes={effectiveMaxMistakes > 0 ? { current: mistakes, max: effectiveMaxMistakes } : null}
        onHelp={() => setGateOpen(true)}
      />

      {/* R1: 旧 justify-end（下寄せ変則）を是正し、上から通常配置。
          証言＋証拠リストは overflow-y-auto で 5 件超でも画面外に切れない。 */}
      <div className="absolute inset-0 flex flex-col px-4 pb-4 pt-14">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pr-1">
          <div className="flex flex-col gap-2">
            <p className="t11-section-label text-xs">証言</p>
            <div className="flex flex-col gap-2">
              {resolvedConfig.statements.map((stmt) => (
                <button
                  key={stmt.id}
                  className={`secondary-btn text-left transition-all ${
                    selectedStatement === stmt.id
                      ? 't11-statement--selected ring-2 scale-[1.02]'
                      : 't11-statement--idle'
                  }`}
                  onClick={() => setSelectedStatement(stmt.id)}
                  disabled={gameState !== 'playing'}
                >
                  {stmt.text}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="t11-section-label text-xs">証拠</p>
            <div className="flex flex-col gap-2">
              {resolvedConfig.evidence.map((ev) => (
                <button
                  key={ev.id}
                  className={`secondary-btn text-left transition-all ${
                    selectedEvidence === ev.id
                      ? 't11-evidence--selected ring-2 scale-[1.02]'
                      : 't11-statement--idle'
                  }`}
                  onClick={() => setSelectedEvidence(ev.id)}
                  disabled={gameState !== 'playing'}
                >
                  {ev.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* プレイ中の瞬間フィードバック（hintPosition: bottom-center 準拠の浮きトースト。
          リスト内に置くとレイアウトが跳ねるため流れの外へ出した） */}
      {feedback && (
        <div className="absolute bottom-3 inset-x-0 z-30 flex justify-center px-4 pointer-events-none">
          <p
            className={`t11-feedback-toast text-xs px-3 py-1.5 rounded-full ${
              feedback.type === 'success' ? 't11-feedback--success' : 't11-feedback--error'
            }`}
          >
            {feedback.message}
          </p>
        </div>
      )}

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          CheckCircle は icons namespace 不在のため同義名 CircleCheck を採用。
          submit('success') 呼び出しは不変（R1 で retryCount を同梱） */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title="矛盾の指摘完了"
        description="すべての矛盾をつなげた"
        icon={<Icon name="CircleCheck" size={40} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />

      {/* R1: 独自 fail オーバーレイ（やり直す / 失敗として送信）を FailOverlay semantic へ移行。
          retry=reset（retryCount 加算）、proceed=submit('fail')（文言は共通既定「未整理のまま進む」） */}
      {gameState === 'fail' && (
        <FailOverlay
          title="矛盾の指摘に失敗"
          subtitle="別の組み合わせを試そう"
          onRetry={reset}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* プレイ中の瞬間フィードバック「異議あり！」スラム（成功モーダルではないため温存）。
          色（背景グラデ・文字色・グロー shadow）は .t11-objection* にトークン化。
          transform -rotate-6 は JSX に残し、keyframes は .t11- セクションの
          t11-objection-slam（transform/opacity のみ）を参照する（R1 で未定義バグを修理） */}
      {showObjection && (
        <div className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none">
          <div
            className="t11-objection px-8 py-4 rounded-lg transform -rotate-6"
            style={{ animation: 't11-objection-slam 0.3s ease-out' }}
          >
            <span className="t11-objection-text text-3xl font-black tracking-wider">
              異議あり！
            </span>
          </div>
        </div>
      )}

      {/* R1 体験文法: 開始ゲート（タイトル＋ルール一行＋始める）。? ボタンで再表示できる */}
      {gateOpen && (
        <StartGate
          title={title}
          instruction="証言と証拠を選び、矛盾を突き付けろ"
          icon="Scale"
          onStart={() => setGateOpen(false)}
        />
      )}

      {showEffect === 'objection' && (
        <SuccessEffect type="star" duration={800} onComplete={() => {}} />
      )}
      {showEffect === 'success' && (
        <SuccessEffect type="confetti" duration={1000} onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'fail' && (
        <FailureEffect type="shake" duration={400} onComplete={() => {}} />
      )}
    </div>
  );
};

export default Template11ContradictionMatch;
